import { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

const SEVERITY_CONFIG = {
  high:   { label: 'High',   bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  medium: { label: 'Medium', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  low:    { label: 'Low',    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};

const THREAT_CONFIG = {
  dox:      { label: 'Dox Alert',  icon: '⚠', bg: '#fef2f2', color: '#dc2626' },
  leak:     { label: 'Paste Leak', icon: '📋', bg: '#eff6ff', color: '#2563eb' },
  dark_web: { label: 'Dark Web',   icon: '🕸', bg: '#faf5ff', color: '#7c3aed' },
};

export default function ThreatPanel({ creatorId }) {
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchThreats();
    const interval = setInterval(fetchThreats, 300000);
    return () => clearInterval(interval);
  }, [filter]);

  async function fetchThreats() {
    setLoading(true);
    try {
      const token = localStorage.getItem('tribal_token');
      if (!token) { setThreats([]); return; }
      const r = await axios.get(`${API}/api/threats`, {
        params: { filter },
        headers: { Authorization: 'Bearer ' + token },
      });
      setThreats(r.data.threats || []);
    } catch (e) {
      console.warn('[ThreatPanel] fetch failed:', e.message);
      setThreats([]);
    } finally {
      setLoading(false);
    }
  }

  async function markAcknowledged(id) {
    try {
      const token = localStorage.getItem('tribal_token');
      await axios.post(`${API}/api/threats/${id}/acknowledge`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
    } catch (e) { console.warn('[ThreatPanel] ack failed:', e.message); }
    fetchThreats();
  }

  async function markResolved(id) {
    try {
      const token = localStorage.getItem('tribal_token');
      await axios.post(`${API}/api/threats/${id}/resolve`, {}, {
        headers: { Authorization: 'Bearer ' + token },
      });
    } catch (e) { console.warn('[ThreatPanel] resolve failed:', e.message); }
    fetchThreats();
  }

  const unresolved = threats.filter(t => !t.resolved_at);
  const highCount = unresolved.filter(t => t.severity === 'high').length;
  const doxCount  = unresolved.filter(t => t.threat_type === 'dox').length;
  const darkCount = unresolved.filter(t => t.threat_type === 'dark_web').length;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#111' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Threat Monitor</h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Dox alerts, paste leaks, and dark web detections</p>
        </div>
        <button onClick={fetchThreats} style={{ fontSize: 13, padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', color: '#374151' }}>Refresh</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'High severity', value: highCount, bg: '#fef2f2', color: '#dc2626' },
          { label: 'Dox alerts',    value: doxCount,  bg: '#fef2f2', color: '#dc2626' },
          { label: 'Dark web hits', value: darkCount, bg: '#faf5ff', color: '#7c3aed' },
        ].map(card => (
          <div key={card.label} style={{ background: card.value > 0 ? card.bg : '#f9fafb', borderRadius: 10, padding: '14px 16px', border: `1px solid ${card.value > 0 ? '#fecaca' : '#e5e7eb'}` }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: card.value > 0 ? card.color : '#9ca3af' }}>{card.value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'dox', 'leak', 'dark_web'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 20, border: '1px solid', borderColor: filter === f ? '#111' : '#e5e7eb', background: filter === f ? '#111' : '#fff', color: filter === f ? '#fff' : '#6b7280', cursor: 'pointer', fontWeight: filter === f ? 600 : 400 }}>
            {f === 'all' ? 'All threats' : f === 'dark_web' ? 'Dark web' : f === 'dox' ? 'Dox alerts' : 'Paste leaks'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>Scanning for threats...</div>
      ) : threats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, background: '#f9fafb', borderRadius: 12, border: '1px dashed #e5e7eb' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 4 }}>No threats detected</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Monitors are running. You'll be alerted if anything is found.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {threats.map(threat => {
            const tc = THREAT_CONFIG[threat.threat_type] || THREAT_CONFIG.leak;
            const sc = SEVERITY_CONFIG[threat.severity] || SEVERITY_CONFIG.medium;
            const isExpanded = expanded === threat.id;
            const isResolved = !!threat.resolved_at;
            const isAcknowledged = !!threat.acknowledged_at;
            return (
              <div key={threat.id} style={{ background: isResolved ? '#f9fafb' : '#fff', border: `1px solid ${isResolved ? '#e5e7eb' : sc.border}`, borderRadius: 10, overflow: 'hidden', opacity: isResolved ? 0.6 : 1 }}>
                <div style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }} onClick={() => setExpanded(isExpanded ? null : threat.id)}>
                  <div style={{ fontSize: 18, marginTop: 1 }}>{tc.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: tc.bg, color: tc.color }}>{tc.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{sc.label}</span>
                      {isResolved && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#f0fdf4', color: '#16a34a' }}>Resolved</span>}
                      {isAcknowledged && !isResolved && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#eff6ff', color: '#2563eb' }}>Acknowledged</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginBottom: 2 }}>
                      <span style={{ fontWeight: 500 }}>{threat.source_platform}</span>
                      {threat.matched_term && <span style={{ color: '#6b7280' }}> · matched "{threat.matched_term}"</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>{threat.source_url}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(threat.detected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{isExpanded ? '▲' : '▼'}</div>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '14px 16px', background: '#fafafa' }}>
                    {threat.context_snippet && (
                      <div style={{ fontSize: 12, color: '#4b5563', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.6 }}>
                        <span style={{ fontWeight: 600, color: '#111', display: 'block', marginBottom: 4 }}>Context</span>
                        {threat.context_snippet}
                      </div>
                    )}
                    <div style={{ fontSize: 12, marginBottom: 14 }}>
                      <span style={{ fontWeight: 600, color: '#111' }}>Source: </span>
                      <a href={threat.source_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all' }} onClick={e => e.stopPropagation()}>{threat.source_url}</a>
                    </div>
                    {!isResolved && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {!isAcknowledged && (
                          <button onClick={e => { e.stopPropagation(); markAcknowledged(threat.id); }} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}>Acknowledge</button>
                        )}
                        <button onClick={e => { e.stopPropagation(); markResolved(threat.id); }} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontWeight: 500 }}>Mark Resolved</button>
                        <a href={`mailto:abuse@${(threat.source_platform || '').replace('site:','').trim()}?subject=DMCA%20Takedown%20Request`} onClick={e => e.stopPropagation()} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 500, textDecoration: 'none' }}>File DMCA</a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
